import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiGet, apiPost } from 'api-service';
import { isArray, toast } from 'lib';
import { getContentPayload } from 'store/content/contentHelpers';
import translation from 'translation';
import {
  getContentRequestQAndQueryId,
  getRequestTimeDuration,
  magicNumbers,
  mapToApiPlatform,
} from 'utils';

const { t } = translation;

const BULLETIN_CONTENT_QUERY_OMIT_KEYS = ['api_path', 'page', 'count'];

const getApiErrorMessage = (error) => {
  const data = error?.response?.data?.errors?.data;
  if (data?.message) return data.message;

  const fieldErrors = data?.errors;
  if (!fieldErrors || typeof fieldErrors !== 'object') return undefined;

  const firstFieldMessages = Object.values(fieldErrors).find(
    (messages) => isArray(messages) && messages.length > 0
  );

  return firstFieldMessages?.[0];
};

const showBulletinContentErrorToast = (error) => {
  const message = getApiErrorMessage(error);

  toast({
    title: t('content:error.bulletinExport.submitTitle'),
    description: message || t('content:error.bulletinExport.submitDescription'),
    type: 'error',
  });
};

const isEmptyBulletinParamValue = (value) =>
  value === undefined ||
  value === null ||
  (isArray(value) && value.length === 0);

const buildBulletinContentQueryParams = (contentPayload) =>
  Object.fromEntries(
    Object.entries(contentPayload).filter(
      ([key, value]) =>
        !BULLETIN_CONTENT_QUERY_OMIT_KEYS.includes(key) &&
        !isEmptyBulletinParamValue(value)
    )
  );

const getStoriesBulletinPayload = (thunkAPI) => {
  const { currentDate, currentQuery } = thunkAPI.getState().pageReducer;
  const { filters } = thunkAPI.getState().storyReducer;
  const { q, queryId } = getContentRequestQAndQueryId({ currentQuery });

  return {
    sort: 'time',
    want_vip_flag: 1,
    from: currentDate.timeDuration ? undefined : currentDate.fromDate,
    to: currentDate.timeDuration ? undefined : currentDate.toDate,
    time_duration: getRequestTimeDuration(currentDate),
    query: queryId,
    q,
    [filters?.resourcesFilter?.selectedValue === 'include'
      ? 'resource_ids'
      : 'not_resource_ids']: Object.keys(
      filters?.resourcesFilter?.selectedItems || {}
    ),
    [filters?.resourceCategoryFilter?.filterType === 'include'
      ? 'tag_id'
      : 'not_tag_id']:
      filters?.resourceCategoryFilter?.selectedValue || undefined,
  };
};

export const fetchBulletinListForExport = createAsyncThunk(
  'bulletinContentQueryReducer/fetchBulletinListForExport',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet('/bulletin', {
        no_paginate: 1,
        is_archive: 0,
      });
      return response.data?.result?.data || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createBulletinForExport = createAsyncThunk(
  'bulletinContentQueryReducer/createBulletinForExport',
  async (title, thunkAPI) => {
    try {
      const response = await apiPost('/bulletin/v2', { title });
      return response.data?.result?.data;
    } catch (error) {
      const message = getApiErrorMessage(error);

      if (message?.includes('bulletins exceeds the limit')) {
        toast({
          title: t('content:error.bulletinExport.createBulletinTitle'),
          description: t(
            'content:error.bulletinExport.createBulletinDescription'
          ),
          type: 'error',
        });
      } else {
        toast({
          title: t('content:error.bulletinExport.createBulletinTitle'),
          description: t('content:error.bulletinExport.submitDescription'),
          type: 'error',
        });
      }

      return thunkAPI.rejectWithValue(error);
    }
  }
);

const buildBulletinContentQueryPayload = (thunkAPI, payload) => {
  const { platform, count, bulletinId } = payload;
  const contentPayload =
    platform === 'instagramStories'
      ? getStoriesBulletinPayload(thunkAPI)
      : getContentPayload(platform, thunkAPI);

  return {
    bulletin_ids: [bulletinId],
    count,
    platform: mapToApiPlatform[platform],
    params: buildBulletinContentQueryParams(contentPayload),
  };
};

export const addContentToBulletinByQuery = createAsyncThunk(
  'bulletinContentQueryReducer/addContentToBulletinByQuery',
  async (payload, thunkAPI) => {
    const requestData = buildBulletinContentQueryPayload(thunkAPI, payload);

    try {
      const response = await apiPost('/bulletin/content/query', requestData);
      return {
        data: response.data?.result?.data,
        bulletinId: payload.bulletinId,
      };
    } catch (error) {
      showBulletinContentErrorToast(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addContentToBulletinByIds = createAsyncThunk(
  'bulletinContentQueryReducer/addContentToBulletinByIds',
  async (payload, thunkAPI) => {
    const { platform, bulletinId, ids } = payload;

    try {
      const response = await apiPost('/bulletin/content', {
        bulletin_ids: [bulletinId],
        post_ids: isArray(ids) ? ids : [ids],
        platform: mapToApiPlatform[platform],
      });
      return {
        data: response.data?.result?.data,
        bulletinId,
      };
    } catch (error) {
      showBulletinContentErrorToast(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const initialState = {
  isModalOpen: false,
  platform: '',
  count: magicNumbers.MAX_BULLETIN_CONTENT_QUERY_COUNT,
  activeStep: 'selectBulletin',
  selectedBulletinId: null,
  newBulletinTitle: '',
  bulletinList: {
    data: [],
    loading: false,
    error: null,
  },
  createBulletin: {
    loading: false,
    error: null,
  },
  submit: {
    loading: false,
    error: null,
  },
};

export const bulletinContentQuerySlice = createSlice({
  name: 'bulletinContentQuerySlice',
  initialState,
  reducers: {
    openBulletinContentQueryModal: (state, action) => {
      const { platform } = action.payload;
      state.isModalOpen = true;
      state.platform = platform;
      state.activeStep = 'selectBulletin';
      state.selectedBulletinId = null;
      state.newBulletinTitle = '';
      state.submit.error = null;
      state.createBulletin.error = null;
    },
    closeBulletinContentQueryModal: (state) => {
      state.isModalOpen = false;
      state.activeStep = 'selectBulletin';
      state.selectedBulletinId = null;
      state.newBulletinTitle = '';
      state.submit.error = null;
      state.createBulletin.error = null;
    },
    changeBulletinContentQueryValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setBulletinContentQueryStep: (state, action) => {
      state.activeStep = action.payload;
    },
    resetBulletinContentQueryForm: (state) => {
      state.count = magicNumbers.MAX_BULLETIN_CONTENT_QUERY_COUNT;
      state.selectedBulletinId = null;
      state.newBulletinTitle = '';
      state.activeStep = 'selectBulletin';
      state.submit.error = null;
      state.createBulletin.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBulletinListForExport.fulfilled, (state, action) => {
        state.bulletinList.data = action.payload;
        state.bulletinList.loading = false;
        state.bulletinList.error = null;
        if (action.payload?.length > 0) {
          state.selectedBulletinId = action.payload[0]?.id;
          state.activeStep = 'selectBulletin';
        } else {
          state.activeStep = 'createBulletin';
        }
      })
      .addCase(fetchBulletinListForExport.pending, (state) => {
        state.bulletinList.loading = true;
        state.bulletinList.error = null;
      })
      .addCase(fetchBulletinListForExport.rejected, (state, action) => {
        state.bulletinList.loading = false;
        state.bulletinList.error = action.payload;
      })
      .addCase(createBulletinForExport.fulfilled, (state, action) => {
        state.createBulletin.loading = false;
        state.createBulletin.error = null;
        state.bulletinList.data.unshift({
          id: action.payload?.id,
          title: state.newBulletinTitle,
        });
        state.selectedBulletinId = action.payload?.id;
        state.activeStep = 'selectBulletin';
      })
      .addCase(createBulletinForExport.pending, (state) => {
        state.createBulletin.loading = true;
        state.createBulletin.error = null;
      })
      .addCase(createBulletinForExport.rejected, (state, action) => {
        state.createBulletin.loading = false;
        state.createBulletin.error = action.payload;
      })
      .addCase(addContentToBulletinByQuery.fulfilled, (state) => {
        state.submit.loading = false;
        state.submit.error = null;
        state.isModalOpen = false;
      })
      .addCase(addContentToBulletinByQuery.pending, (state) => {
        state.submit.loading = true;
        state.submit.error = null;
      })
      .addCase(addContentToBulletinByQuery.rejected, (state, action) => {
        state.submit.loading = false;
        state.submit.error = action.payload;
      })
      .addCase(addContentToBulletinByIds.fulfilled, (state) => {
        state.submit.loading = false;
        state.submit.error = null;
        state.isModalOpen = false;
      })
      .addCase(addContentToBulletinByIds.pending, (state) => {
        state.submit.loading = true;
        state.submit.error = null;
      })
      .addCase(addContentToBulletinByIds.rejected, (state, action) => {
        state.submit.loading = false;
        state.submit.error = action.payload;
      });
  },
});

export const {
  openBulletinContentQueryModal,
  closeBulletinContentQueryModal,
  changeBulletinContentQueryValue,
  setBulletinContentQueryStep,
  resetBulletinContentQueryForm,
} = bulletinContentQuerySlice.actions;
